import { AgentGatewayService } from "@/services/agent-gateway-service";
import {
  AgentStreamConnectionError,
  AgentStreamTerminalError,
} from "@/services/agent-stream-errors";
import type { AgentRun, AgentSession } from "@/services/types/agent-gateway";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ComposerStatus } from "./AgentComposer";
import { classifyAgentGatewayFailure } from "./agent-gateway-failure";
import {
  type OutgoingMessageAttempt,
  type StreamingMessage,
  createMessageAttempt,
  mergeNormalizedEvents,
} from "./message-utils";
import { agentGatewayQueryKeys } from "./query-options";

type SendMessageVariables = {
  agentKey: string;
  sessionId: string;
  attempt: OutgoingMessageAttempt;
};

type ActiveRequest = {
  scope: string;
  runId: string;
  controller: AbortController;
};

type InFlightSubmission = {
  scope: string;
  idempotencyKey: string;
};

const STREAM_RECOVERY_DELAYS_MS = [250, 750, 1_500, 3_000] as const;
const WORKFLOW_START_IDEMPOTENCY_KEY = "workflow-start-v2";
const WORKFLOW_START_LABEL = "Start lead research";
const ACTIVE_RUN_STATUSES = new Set(["queued", "running", "cancel_requested"]);

function waitForStreamRecovery(delayMs: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(
        signal.reason ??
          new DOMException("The request was aborted.", "AbortError"),
      );
      return;
    }

    const timeoutId = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", handleAbort);
      resolve();
    }, delayMs);

    function handleAbort() {
      globalThis.clearTimeout(timeoutId);
      reject(
        signal.reason ??
          new DOMException("The request was aborted.", "AbortError"),
      );
    }

    signal.addEventListener("abort", handleAbort, { once: true });
  });
}

function terminalRunError(run: AgentRun) {
  return new AgentStreamTerminalError(
    run.error?.code ?? "run_interrupted",
    run.error?.message ?? "The agent run ended unexpectedly.",
    run.correlation_id,
  );
}

export function useAgentMessageController(
  agentKey: string,
  sessionId: string,
  currentRun: AgentRun | null = null,
) {
  const queryClient = useQueryClient();
  const activeScopeRef = useRef<string | null>(`${agentKey}:${sessionId}`);
  const activeRequestRef = useRef<ActiveRequest | null>(null);
  const submissionInFlightRef = useRef<InFlightSubmission | null>(null);
  const currentRunRef = useRef(currentRun);
  currentRunRef.current = currentRun;
  const resumableRunId =
    currentRun?.id ?? activeRequestRef.current?.runId ?? null;
  const [draft, setDraftValue] = useState("");
  const [outgoingAttempts, setOutgoingAttempts] = useState<
    OutgoingMessageAttempt[]
  >([]);
  const [streamingMessages, setStreamingMessages] = useState<
    Record<string, StreamingMessage>
  >({});
  const [composerError, setComposerError] = useState<string | null>(null);
  const [composerStatus, setComposerStatus] = useState<ComposerStatus>("idle");
  const [announcement, setAnnouncement] = useState("");

  const sessionQueryKey = useMemo(
    () => agentGatewayQueryKeys.session(agentKey, sessionId),
    [agentKey, sessionId],
  );

  const setCurrentRun = useCallback(
    (run: AgentRun | null) => {
      queryClient.setQueryData<AgentSession>(sessionQueryKey, (session) =>
        session ? { ...session, current_run: run } : session,
      );
    },
    [queryClient, sessionQueryKey],
  );

  const mergeSessionEvents = useCallback(
    (events: AgentSession["events"]) => {
      queryClient.setQueryData<AgentSession>(sessionQueryKey, (session) =>
        session
          ? {
              ...session,
              events: mergeNormalizedEvents(session.events, events),
            }
          : session,
      );
    },
    [queryClient, sessionQueryKey],
  );

  const consumeRun = useCallback(
    async (run: AgentRun, controller: AbortController) => {
      const scope = `${agentKey}:${sessionId}`;
      const seenFrameIds = new Set<string>();
      let lastEventId: string | undefined;
      let recoveryAttempt = 0;

      while (true) {
        try {
          for await (const frame of AgentGatewayService.streamRun(
            agentKey,
            sessionId,
            run.id,
            controller.signal,
            lastEventId,
          )) {
            if (
              controller.signal.aborted ||
              activeScopeRef.current !== scope ||
              activeRequestRef.current?.runId !== run.id
            ) {
              continue;
            }
            if (frame.id) {
              lastEventId = frame.id;
              if (seenFrameIds.has(frame.id)) {
                continue;
              }
              seenFrameIds.add(frame.id);
            }
            recoveryAttempt = 0;

            switch (frame.event) {
              case "message_delta":
                setStreamingMessages((messages) => {
                  const existing = messages[frame.data.message_key];
                  return {
                    ...messages,
                    [frame.data.message_key]: {
                      messageKey: frame.data.message_key,
                      text: `${existing?.text ?? ""}${frame.data.delta}`,
                    },
                  };
                });
                break;
              case "agent_event":
                mergeSessionEvents([frame.data.event]);
                if (frame.data.message_key) {
                  setStreamingMessages((messages) => {
                    const next = { ...messages };
                    delete next[frame.data.message_key as string];
                    return next;
                  });
                }
                break;
              case "complete":
                return;
              case "error":
                throw new AgentStreamTerminalError(
                  frame.data.code,
                  frame.data.message,
                  frame.data.correlation_id,
                );
            }
          }
          throw new AgentStreamConnectionError();
        } catch (error) {
          if (controller.signal.aborted || activeScopeRef.current !== scope) {
            throw error;
          }
          if (error instanceof AgentStreamTerminalError) {
            throw error;
          }

          let latestRun: AgentRun | null = null;
          try {
            latestRun = await AgentGatewayService.getRun(
              agentKey,
              sessionId,
              run.id,
            );
          } catch {
            // The status request shares the same transient connection risk.
          }

          if (latestRun && !ACTIVE_RUN_STATUSES.has(latestRun.status)) {
            if (latestRun.status === "completed") {
              return;
            }
            throw terminalRunError(latestRun);
          }

          recoveryAttempt += 1;
          const delay =
            STREAM_RECOVERY_DELAYS_MS[
              Math.min(
                recoveryAttempt - 1,
                STREAM_RECOVERY_DELAYS_MS.length - 1,
              )
            ];
          setAnnouncement("Connection interrupted. Reconnecting to the run.");
          await waitForStreamRecovery(delay, controller.signal);
        }
      }
    },
    [agentKey, mergeSessionEvents, sessionId],
  );

  const messageMutation = useMutation({
    mutationFn: async ({ attempt }: SendMessageVariables) => {
      const scope = `${agentKey}:${sessionId}`;
      const run = await AgentGatewayService.createRun(
        agentKey,
        sessionId,
        attempt.requestKind === "workflow_start"
          ? { operation: "workflow_start" }
          : { operation: "message", text: attempt.text },
        attempt.idempotencyKey,
      );
      setCurrentRun(run);

      if (activeScopeRef.current !== scope) {
        return run;
      }

      const controller = new AbortController();
      activeRequestRef.current = { scope, runId: run.id, controller };
      await consumeRun(run, controller);
      return run;
    },
    onSuccess: (run, variables) => {
      const scope = `${variables.agentKey}:${variables.sessionId}`;
      if (
        submissionInFlightRef.current?.scope === scope &&
        submissionInFlightRef.current.idempotencyKey ===
          variables.attempt.idempotencyKey
      ) {
        submissionInFlightRef.current = null;
      }
      if (activeRequestRef.current?.runId === run.id) {
        activeRequestRef.current = null;
      }
      if (activeScopeRef.current === scope) {
        setCurrentRun(null);
        setStreamingMessages({});
        setOutgoingAttempts((attempts) =>
          attempts.filter(
            (item) => item.idempotencyKey !== variables.attempt.idempotencyKey,
          ),
        );
        setComposerError(null);
        setComposerStatus("completed");
        setAnnouncement(
          variables.attempt.requestKind === "workflow_start"
            ? "Lead research started."
            : "Message completed.",
        );
      }
      void queryClient.invalidateQueries({
        exact: true,
        queryKey: sessionQueryKey,
      });
      void queryClient.invalidateQueries({
        exact: true,
        queryKey: agentGatewayQueryKeys.sessions(variables.agentKey),
      });
    },
    onError: (error, variables) => {
      const scope = `${variables.agentKey}:${variables.sessionId}`;
      const failure = classifyAgentGatewayFailure(error);
      submissionInFlightRef.current = null;
      activeRequestRef.current = null;

      void queryClient.invalidateQueries({
        exact: true,
        queryKey: sessionQueryKey,
      });
      if (activeScopeRef.current !== scope) {
        return;
      }

      setStreamingMessages({});
      setOutgoingAttempts((attempts) =>
        attempts.map((item) =>
          item.idempotencyKey === variables.attempt.idempotencyKey
            ? {
                ...item,
                status: "failed",
                errorMessage: failure.message,
                failureKind: failure.kind,
                retryable: failure.retryable,
              }
            : item,
        ),
      );
      setComposerError(null);
      setComposerStatus("failed");
      setAnnouncement(failure.message);
    },
  });

  useEffect(() => {
    const scope = `${agentKey}:${sessionId}`;
    activeScopeRef.current = scope;
    submissionInFlightRef.current = null;
    setDraftValue("");
    setOutgoingAttempts([]);
    setStreamingMessages({});
    setComposerError(null);
    setComposerStatus("idle");
    setAnnouncement("");

    return () => {
      if (activeScopeRef.current === scope) {
        activeScopeRef.current = null;
      }
      if (activeRequestRef.current?.scope === scope) {
        activeRequestRef.current.controller.abort();
        activeRequestRef.current = null;
      }
      submissionInFlightRef.current = null;
    };
  }, [agentKey, sessionId]);

  useEffect(() => {
    const resumableRun = currentRunRef.current;
    if (
      !resumableRun ||
      resumableRun.id !== resumableRunId ||
      !ACTIVE_RUN_STATUSES.has(resumableRun.status) ||
      activeRequestRef.current ||
      submissionInFlightRef.current
    ) {
      return;
    }

    const scope = `${agentKey}:${sessionId}`;
    const controller = new AbortController();
    activeRequestRef.current = { scope, runId: resumableRun.id, controller };
    setComposerStatus("pending");
    setAnnouncement("Reconnected to the active agent run.");

    void consumeRun(resumableRun, controller)
      .then(() => {
        if (activeScopeRef.current !== scope) {
          return;
        }
        activeRequestRef.current = null;
        setCurrentRun(null);
        setStreamingMessages({});
        setComposerStatus("completed");
        setAnnouncement("Agent run completed.");
        void queryClient.invalidateQueries({
          exact: true,
          queryKey: sessionQueryKey,
        });
      })
      .catch((error) => {
        if (controller.signal.aborted || activeScopeRef.current !== scope) {
          return;
        }
        activeRequestRef.current = null;
        const failure = classifyAgentGatewayFailure(error);
        setStreamingMessages({});
        setComposerStatus("failed");
        setAnnouncement(failure.message);
        void queryClient.invalidateQueries({
          exact: true,
          queryKey: sessionQueryKey,
        });
      });

    return () => {
      if (activeRequestRef.current?.runId === resumableRun.id) {
        controller.abort();
        activeRequestRef.current = null;
      }
    };
  }, [
    agentKey,
    consumeRun,
    queryClient,
    resumableRunId,
    sessionId,
    sessionQueryKey,
    setCurrentRun,
  ]);

  const setDraft = (value: string) => {
    setDraftValue(value);
    setComposerError(null);
    if (composerStatus === "completed" || composerStatus === "failed") {
      setComposerStatus("idle");
      setAnnouncement("");
    }
  };

  const submitMessage = (
    text: string,
    options: {
      idempotencyKey?: string;
      requestKind?: "message" | "workflow_start";
    } = {},
  ) => {
    if (submissionInFlightRef.current || activeRequestRef.current) {
      return;
    }
    const currentSession =
      queryClient.getQueryData<AgentSession>(sessionQueryKey);
    const attempt = createMessageAttempt(text, {
      afterEventId: currentSession?.events.at(-1)?.id ?? null,
      ...options,
    });
    submissionInFlightRef.current = {
      scope: `${agentKey}:${sessionId}`,
      idempotencyKey: attempt.idempotencyKey,
    };
    setDraftValue("");
    setComposerError(null);
    setComposerStatus("pending");
    setAnnouncement(
      attempt.requestKind === "workflow_start"
        ? "Starting lead research."
        : "Sending message.",
    );
    setOutgoingAttempts((attempts) => [...attempts, attempt]);
    messageMutation.mutate({ agentKey, sessionId, attempt });
  };

  const retryMessage = (attempt: OutgoingMessageAttempt) => {
    if (
      submissionInFlightRef.current ||
      activeRequestRef.current ||
      attempt.status !== "failed" ||
      !attempt.retryable
    ) {
      return;
    }
    submissionInFlightRef.current = {
      scope: `${agentKey}:${sessionId}`,
      idempotencyKey: attempt.idempotencyKey,
    };
    setComposerStatus("pending");
    setAnnouncement("Retrying message.");
    setOutgoingAttempts((attempts) =>
      attempts.map((item) =>
        item.idempotencyKey === attempt.idempotencyKey
          ? { ...attempt, status: "pending" }
          : item,
      ),
    );
    messageMutation.mutate({ agentKey, sessionId, attempt });
  };

  return {
    announcement,
    composerError,
    composerStatus,
    draft,
    isPending:
      composerStatus === "pending" ||
      Boolean(currentRun && ACTIVE_RUN_STATUSES.has(currentRun.status)),
    outgoingAttempts,
    retryMessage,
    sendMessage: (text: string) => submitMessage(text),
    setDraft,
    startWorkflow: () =>
      submitMessage(WORKFLOW_START_LABEL, {
        idempotencyKey: WORKFLOW_START_IDEMPOTENCY_KEY,
        requestKind: "workflow_start",
      }),
    streamingMessages: Object.values(streamingMessages),
  };
}
